import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Button, Menu, MenuItem, SpeedDialIcon } from '@mui/material';
import classNames from 'classnames';
import { Fragment, useState } from 'react';

import config, { Language } from '../../config';
import { useLanguage } from '../../hooks/useLanguage';
import useLoggedIn from '../../hooks/useLoggedIn';
import { GTM } from '../../utils/analytics';

type LanguageDropdownProps = {
  className?: string;
};

export const LanguageDropdown = (props: LanguageDropdownProps) => {
  const { className } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const {
    language: currentLanguage,
    availableLanguages,
    currentUserCountry,
    setLanguage,
    getLanguageWithTranslationLabel,
    getLanguageShortName,
  } = useLanguage();
  const isLoggedIn = useLoggedIn();

  // Hide dropdown if user's current country has less than 2 languages available
  if (!availableLanguages || availableLanguages.length < 2) {
    return null;
  }

  const onSelect = async (language: Language) => {
    handleClose();

    await setLanguage({
      language,
      country: currentUserCountry || config.country,
      saveToDb: isLoggedIn,
    });

    GTM.updateUserProperties({
      user_language: language,
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Fragment>
      <Button
        className={classNames(className)}
        aria-controls={open ? 'language-selector-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        disableElevation
        onClick={handleClick}
        endIcon={<SpeedDialIcon open={open} icon={<KeyboardArrowDownIcon />} openIcon={<KeyboardArrowUpIcon />} />}
        sx={{ color: '#757575', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
      >
        {getLanguageShortName(currentLanguage)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        classes={{ list: 'language-dropdown-menu' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {availableLanguages.map((language: Language) => (
          <MenuItem
            key={'language-selector-' + language}
            className={classNames('language-dropdown-item', { checked: language === currentLanguage })}
            value={language}
            onClick={() => onSelect(language)}
          >
            {getLanguageWithTranslationLabel(language)}

            {language === currentLanguage && <CheckIcon className="check-icon" />}
          </MenuItem>
        ))}
      </Menu>
    </Fragment>
  );
};
